/**
 * `useOpBlockStore` — the tiny state behind the IDE-wide "operation in
 * progress" blocking overlay. Resource copy/move ops are infrequent but not
 * instant (they carry the sidecar set + reindex the destination), and the
 * fast paths (send-to popover, drag-drop) have no natural in-flight surface —
 * so while one runs we block the whole IDE with a centered spinner
 * (operator brief 2026-07-03) until the op AND its tree refetch complete.
 *
 * Ref-counted so a stray double-begin can't leave the overlay stuck; `kind`
 * picks the op-specific label. Driven from `useResourceCopyMove` (the single
 * mutation every copy/move surface funnels through).
 */

import { create } from "zustand";

export type OpBlockKind = "copy" | "move";

interface OpBlockState {
  /** Number of in-flight blocking ops (0 → overlay hidden). */
  pending: number;
  /** The current op kind, for the label. Null when idle. */
  kind: OpBlockKind | null;
  begin: (kind: OpBlockKind) => void;
  end: () => void;
  /** Force the overlay down regardless of ref-count — the watchdog backstop so
   *  a lifecycle gap (e.g. an offline-paused mutation) can't wedge the IDE. */
  reset: () => void;
}

export const useOpBlockStore = create<OpBlockState>((set) => ({
  pending: 0,
  kind: null,
  begin: (kind) => set((s) => ({ pending: s.pending + 1, kind })),
  end: () =>
    set((s) => {
      const pending = Math.max(0, s.pending - 1);
      return { pending, kind: pending === 0 ? null : s.kind };
    }),
  reset: () => set({ pending: 0, kind: null }),
}));
